<template>
  <div v-if="bookingWeek.defaultSiteSchedule">
    <div
      v-if="!showControls"
      class="row q-gutter-sm q-mb-md justify-end"
    >
      <q-slider
        v-model.number="bookingWeek.pixelsPerMinute"
        type="number"
        class="col-1 q-my-xs q-mx-md"
        :min="1"
        :max="5"
        :step="0.1"
      />
    </div>
    <div
      v-if="showControls"
      class="row q-gutter-md non-selectable q-mb-md items-end"
    >
      <div class="col-auto">
        <div class="button-title q-mt-none q-mb-sm">
          Actions
        </div>
        <div class="row q-gutter-sm">
          <q-btn
            size="sm"
            class="q-my-xs q-px-xs"
            style="min-width: 64px;"
            dense
            outline
            :disable="bookingWeek.convertDisplayedToShowingsCount === 0"
            :label="'Build' + (bookingWeek.convertDisplayedToShowingsCount > 0 ? ' (' + bookingWeek.convertDisplayedToShowingsCount + ')' : '')"
            @click="bookingWeek.convertDisplayedToShowings()"
          >
            <q-tooltip>
              Convert plan elements to showings
            </q-tooltip>
          </q-btn>
          <q-btn
            size="sm"
            class="q-my-xs q-px-xs"
            style="min-width: 74px;"
            dense
            outline
            :disable="bookingWeek.removeDisplayedShowingsCount === 0"
            :label="'Remove' + (bookingWeek.removeDisplayedShowingsCount > 0 ? ' (' + bookingWeek.removeDisplayedShowingsCount + ')' : '')"
            @click="bookingWeek.removeDisplayedShowings()"
          >
            <q-tooltip>
              Remove the current Showings
            </q-tooltip>
          </q-btn>
          <q-btn
            size="sm"
            class="q-my-xs q-px-xs"
            style="min-width: 79px;"
            dense
            outline
            :disable="bookingWeek.publishDisplayedShowingsCount === 0"
            :label="'Publish' + (bookingWeek.publishDisplayedShowingsCount > 0 ? ' (' + bookingWeek.publishDisplayedShowingsCount + ')' : '')"
            @click="bookingWeek.publishDisplayedShowings()"
          >
            <q-tooltip>
              Publish the current Showings
            </q-tooltip>
          </q-btn>
          <q-btn
            size="sm"
            class="q-my-xs q-px-xs"
            style="min-width: 90px;"
            dense
            outline
            :disable="bookingWeek.unpublishDisplayedShowingsCount === 0"
            :label="'Unpublish' + (bookingWeek.unpublishDisplayedShowingsCount > 0 ? ' (' + bookingWeek.unpublishDisplayedShowingsCount + ')' : '')"
            @click="bookingWeek.unpublishDisplayedShowings()"
          >
            <q-tooltip>
              Unpublish the current Showings
            </q-tooltip>
          </q-btn>
        </div>
      </div>
      <q-btn-group
        outline
        dense
        style="margin-top: -5px; margin-bottom: -5px;"
      >
        <q-btn
          dense
          size="sm"
          class="q-my-xs"
          outline
          :class="{ 'selected-button': !bookingWeek.paintValue }"
          @click="bookingWeek.paintValue = null"
        >
          <div class="row no-wrap justify-center q-gutter-sm items-center">
            <q-icon
              name="highlight_alt"
              size="sm"
            />
          </div>
          <q-tooltip>
            Selector
          </q-tooltip>
        </q-btn>
        <q-btn
          v-if="currentLayoutSlot"
          dense
          :disable="!previousPaintValue"
          size="sm"
          class="q-my-xs"
          outline
          @click="bookingWeek.paintValue = previousPaintValue"
        >
          <q-icon name="navigate_before" />
          <q-tooltip>
            Previous Title
          </q-tooltip>
        </q-btn>
        <q-btn
          dense
          size="sm"
          class="q-my-xs"
          :class="currentLayoutSlot ? 'selected-button' : ''"
          outline
          @click="showTitleSlotDialog = true"
        >
          <layout-slot
            :layout-slot="currentLayoutSlot || firstLayoutSlot"
            :display-mode="displayMode"
            size="sm"
          />
          <q-icon name="more_horiz">
            <q-tooltip>
              Select Title
            </q-tooltip>
          </q-icon>
        </q-btn>
        <q-btn
          v-if="currentLayoutSlot"
          :disable="!nextPaintValue"
          dense
          size="sm"
          class="q-my-xs"
          outline
          @click="bookingWeek.paintValue = nextPaintValue"
        >
          <q-icon name="navigate_next" />
          <q-tooltip>
            Next Title
          </q-tooltip>
        </q-btn>
      </q-btn-group>

      <div v-if="bookingWeek.by === 'screen'">
        <div class="button-title">
          Screen
        </div>
        <q-btn-toggle
          v-model="bookingWeek.screenFilter"
          size="sm"
          class="q-my-xs"
          dense
          outline
          toggle-color="primary"
          :options="screenFilterOptions"
        />
      </div>

      <div v-if="bookingWeek.by === 'day'">
        <div class="button-title">
          Day
        </div>
        <q-btn-toggle
          v-model="bookingWeek.dayFilter"
          size="sm"
          class="q-my-xs"
          dense
          outline
          no-caps
          toggle-color="primary"
          :options="dayFilterOptions"
        />
      </div>

      <q-space />

      <div v-if="bookingWeek.allowShowResources">
        <div class="button-title">
          Audit
        </div>
        <q-btn-toggle
          v-model="bookingWeek.auditView"
          size="sm"
          class="q-my-xs"
          dense
          outline
          no-caps
          toggle-color="primary"
          :options="[
            { label: 'None', value: null },
            { label: 'Resources', value: 'resources' },
            { label: 'Start Times', value: 'start-times' },
          ]"
          :disable="!bookingWeek.showResources && bookingWeek.isDirty && !bookingWeek.auditView"
        >
          <q-tooltip v-if="!bookingWeek.showResources && bookingWeek.isDirty">
            Save your changes to view resources
          </q-tooltip>
        </q-btn-toggle>
      </div>
      <div class="col-1 q-ml-md q-mr-xs">
        <div class="button-title">
          Zoom
        </div>
        <q-slider
          v-model.number="bookingWeek.pixelsPerMinute"
          style="height: 30px;"
          type="number"
          :min="1"
          :max="5"
          :step="0.1"
        />
      </div>
      <q-btn
        round
        outline
        size="md"
        dense
        icon="more_vert"
      >
        <q-menu
          bordered
          side="right"
          overlay
        >
          <q-list>
            <submenu-options
              v-model="bookingWeek.scheduleMode"
              name="Schedule Mode"
              :options="[
                { label: 'Plan and Actual', value: 'plan-and-actual' },
                { label: 'Actual', value: 'actual' },
                { label: 'Published', value: 'published' },
              ]"
            />
            <q-separator />
            <submenu-options
              v-model="bookingWeek.by"
              name="Layout"
              :options="[
                { label: 'By Day', value: 'day' },
                { label: 'By Screen', value: 'screen' }
              ]"
            />
            <submenu-options
              v-model="colorBy"
              name="Color By"
              :options="[
                { label: 'Plan Element', value: 'planElement' },
                { label: 'Movie', value: 'movie' }
              ]"
            />
            <q-separator />
            <submenu-options
              v-model="bookingWeek.snapTo"
              name="Snap To"
              :options="[
                { label: 'None', value: null },
                { label: '5 min', value: 5 },
                { label: '10 min', value: 10 },
                { label: '15 min', value: 15 },
              ]"
            />
            <q-separator />
            <submenu-options
              v-model="bookingWeek.overrideLock"
              name="Override Lock"
              :options="[
                { label: 'On', value: true },
                { label: 'Off', value: false },
              ]"
            />
            <q-separator />
            <q-item
              v-close-popup
              clickable
              @click="legend = true"
            >
              <q-item-section>
                Show Legend
              </q-item-section>
            </q-item>
            <q-item
              v-close-popup
              clickable
              @click="() => $refs.bookingWeekCopyDialog.show()"
            >
              <q-item-section>
                Copy From Another Week
              </q-item-section>
            </q-item>
          </q-list>
        </q-menu>
      </q-btn>
    </div>
    <div class="row non-selectable">
      <div class="col-auto column">
        <div style="height: 70px;" />

        <div v-if="bookingWeek.by === 'day'">
          <div
            v-for="dayNumber in bookingWeek.daysOfWeekNumbers"
            :key="dayNumber"
          >
            <div
              class="text-center text-caption"
              :style="bookingWeek.showResources ? 'height: 120px;' : 'height: 20px; margin-top: -35px; margin-bottom: 15px;'"
            >
              <q-btn
                style="text-decoration: underline;"
                no-caps
                @click="bookingWeek.selectBy({ dayNumber: dayNumber })"
              >
                {{ date.formatDate(date.addToDate(bookingWeek.date, { date: currentCircuit.daysOfWeekNumbers.indexOf(dayNumber) }), 'ddd Do') }}
              </q-btn>
            </div>
            <div
              v-for="screen in bookingWeek.filteredScreens"
              :key="screen.id"
              class="text-center text-caption q-pa-sm"
              :style="{ height: totalItemHeight + 'px' }"
            >
              <q-btn
                size="md"
                no-caps
                @click="bookingWeek.selectBy({ dayNumber: dayNumber, screenId: screen.id })"
              >
                {{ screen.name }}
              </q-btn>
              <p>
                {{ screen.seatCount }} Seats
              </p>
            </div>
          </div>
        </div>
        <div v-if="bookingWeek.by === 'screen'">
          <div
            v-for="screen in bookingWeek.filteredScreens"
            :key="screen.id"
          >
            <div
              class="text-center text-caption q-pa-sm"
              style="height: 20px; margin-top: -35px; margin-bottom: 20px;"
            >
              <q-btn
                style="text-decoration: underline;"
                @click="bookingWeek.selectBy({ screenId: screen.id })"
              >
                {{ screen.name }}
              </q-btn>
              <p
                class="q-pa-none"
                style="margin-top: -10px;"
              >
                {{ screen.seatCount }} Seats
              </p>
            </div>
            <div
              v-for="dayNumber, index in bookingWeek.daysOfWeekNumbers"
              :key="dayNumber"
              class="text-center text-caption q-pa-sm"
              :style="{ height: totalItemHeight + 'px' }"
            >
              <q-btn @click="bookingWeek.selectBy({ dayNumber: dayNumber, screenId: screen.id })">
                {{ currentCircuit.daysOfWeek[index] }}
              </q-btn>
            </div>
          </div>
        </div>
      </div>
      <div
        ref="wrapper"
        class="col wrapper"
        data-test-id="schedule-schedule"
      >
        <div
          ref="schedule"
          class="schedule"
          :style="{ width: scheduleWidth + 'px' }"
          @mousedown="handleDragStart"
          @mouseup="handleDragEnd"
          @mousemove="handleMouseMove"
          @mouseleave="handleMouseLeave"
          @click="handleClick"
        >
          <q-menu
            v-model="contextMenuShown"
            touch-position
            context-menu
            style="max-width: 350px"
          >
            <q-list>
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.selectedWithShowings.length"
                @click="edit()"
              >
                <q-item-section>
                  Edit
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.canConvertToShowings"
                @click="bookingWeek.convertSelectedToShowings()"
              >
                <q-item-section>
                  Build
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.canPublishShowings"
                @click="bookingWeek.publishSelectedShowings()"
              >
                <q-item-section>
                  Publish
                </q-item-section>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                clickable
                @click="bookingWeek.by === 'day' ? bookingWeek.selectBy({ dayNumber: mouseOverDayNumber }) : bookingWeek.selectBy({ dayNumber: mouseOverDayNumber, screenId: mouseOverScreenId })"
              >
                <q-item-section>
                  Select Day
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                @click="bookingWeek.by === 'day' ? bookingWeek.selectBy({ dayNumber: mouseOverDayNumber, screenId: mouseOverScreenId }) : bookingWeek.selectBy({ screnId: mouseOverScreenId })"
              >
                <q-item-section>
                  Select Screen
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :disable="!mouseOverMovieId"
                @click="mouseOverMovieId && bookingWeek.selectBy({ movieId: mouseOverMovieId })"
              >
                <q-item-section>
                  Select Movie
                </q-item-section>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                clickable
                @click="copy"
              >
                <q-item-section>
                  Copy
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :disable="!clipboard.length"
                @click="paste"
              >
                <q-item-section v-if="clipboard.length">
                  Paste ({{ clipboard.length }} showings)
                </q-item-section>
                <q-item-section v-else>
                  Paste
                </q-item-section>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                clickable
                @click="bookingWeek.copyPriorDay(mouseOverDayNumber)"
              >
                <q-item-section>
                  Copy Prior Day
                </q-item-section>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.canAlignTime"
                @click="bookingWeek.alignStart()"
              >
                <q-item-section>
                  Align Start
                </q-item-section>
                <q-item-section avatar>
                  <q-icon name="skip_previous" />
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.canAlignTime"
                @click="bookingWeek.alignEnd()"
              >
                <q-item-section>
                  Align End
                </q-item-section>
                <q-item-section avatar>
                  <q-icon name="skip_next" />
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.canSpaceEvenly"
                @click="bookingWeek.spaceEvenly()"
              >
                <q-item-section>
                  Space Evenly
                </q-item-section>
                <q-item-section avatar>
                  <q-icon name="settings_ethernet" />
                </q-item-section>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                clickable
                @click="bookingWeek.snapToMinutes(5)"
              >
                <q-item-section>
                  Snap to 5 min
                </q-item-section>
                <q-item-section avatar>
                  <q-icon name="time" />
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                @click="bookingWeek.snapToMinutes(10)"
              >
                <q-item-section>
                  Snap to 10 min
                </q-item-section>
                <q-item-section avatar>
                  <q-icon name="time" />
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                @click="bookingWeek.snapToMinutes(15)"
              >
                <q-item-section>
                  Snap to 15 min
                </q-item-section>
                <q-item-section avatar>
                  <q-icon name="time" />
                </q-item-section>
              </q-item>
              <q-separator />
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.canUnpublishShowings"
                @click="bookingWeek.unpublishSelectedShowings()"
              >
                <q-item-section>
                  Unpublish
                </q-item-section>
              </q-item>
              <q-item
                v-if="displayShowingChangeOption"
                v-close-popup
                clickable
                :disable="showingChangeDisabled || bookingWeek.isDirty || needsShowingChangeTemplateId"
                @click="$refs.showingChangeDialog.show(bookingWeek.selected[0].showing)"
              >
                <q-item-section>
                  Modify Showing (Beta)
                </q-item-section>
                <q-tooltip v-if="showingChangeDisabledTooltip">
                  {{ showingChangeDisabledTooltip }}
                </q-tooltip>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.selectedWithShowings.length"
                @click="bookingWeek.removeSelectedShowings()"
              >
                <q-item-section>
                  Removed Showings
                </q-item-section>
              </q-item>
              <q-item
                v-close-popup
                clickable
                :disable="!bookingWeek.config.manualLayout || !bookingWeek.selectedWithoutShowings.length"
                @click="bookingWeek.removeSelectedFromPlan()"
              >
                <q-item-section>
                  Removed From Plan
                </q-item-section>
              </q-item>
            </q-list>
          </q-menu>

          <div
            v-if="dragging"
            style="position: absolute; border: dashed 2px #000; pointer-events: none; z-index: 5"
            :style="{
              top: `${draggingTop}px`,
              left: `${draggingLeft}px`,
              height: `${draggingHeight}px`,
              width: `${draggingWidth}px`
            }"
          />
          <div
            v-for="timeSlot in computedTimeSlots"
            :key="timeSlot.id"
            :style="{
              top: timeSlot.top + 'px',
              left: timeSlot.left + 'px',
              width: timeSlot.width + 'px',
              opacity: (bookingWeek.auditView === 'start-times' || !bookingWeek.auditView) && bookingWeek.paintValue ? 0.3 : 1
            }"
            class="text-center text-caption time-slot text-no-wrap"
            style="line-height: 28px;"
          >
            <div
              class="target-time"
              :style="{ left: (timeSlot.targetTimeOffset - 2) + 'px', }"
            >
              |
            </div>
            {{ timeSlot.name }}
          </div>

          <div
            v-for="hour in hours"
            :key="hour.left"
            :style="{
              top: hour.top,
              left: hour.left,
              width: hour.width
            }"
            class="text-center text-caption label overflow-hidden text-no-wrap"
            style="line-height: 28px;"
          >
            {{ hour.label }}
          </div>

          <template v-if="bookingWeek.showResources">
            <div style="position: absolute; top: 60px; left: 0; right: 0; height: 50px; background-color: #EEE; z-index: 0; border-bottom: solid 1px #CCC" />
            <div style="position: absolute; top: 110px; left: 0; right: 0; height: 52px; background-color: #EEE; z-index: 0" />
            <q-inner-loading
              :showing="taskPlanningForDate.loading"
              style="position: absolute; top: 60px; left: 0; right: 0; height: 102px; z-index: 10"
            />

            <template v-if="bookingWeek.taskPlanningForWeek.data.length">
              <highcharts
                style="position: absolute; pointer-events: all; z-index: 5;"
                :options="bookingWeek.employeeDemandChart.chartData"
                :style="bookingWeek.employeeDemandChart.style"
              />

              <highcharts
                v-for="arriveChart in bookingWeek.arriveCharts"
                :key="'arriveChart' + arriveChart.showingId"
                style="position: absolute; pointer-events: all; z-index: 5"
                :options="arriveChart.chartData"
                :style="arriveChart.style"
              />

              <div
                v-for="cleanTask in bookingWeek.cleanTaskBoxes"
                :key="'cleanTaskBoxes' + cleanTask.top + ',' + cleanTask.left"
                style="position: absolute; pointer-events: all; z-index: 6"
                :style="cleanTask"
              >
                <q-tooltip>
                  Clean Time {{ cleanTask.duration }} min
                </q-tooltip>
              </div>

              <div
                v-for="cleanTask in bookingWeek.cleanTasksScheduled"
                :key="'cleanTasksScheduled' + cleanTask.top + ',' + cleanTask.left"
                :style="cleanTask"
                style="position: absolute;"
              />

              <div
                v-for="cleanTask in bookingWeek.cleanTaskBoxesSummary"
                :key="'cleanTaskBoxes' + cleanTask.top + ',' + cleanTask.left"
                style="position: absolute; pointer-events: all; z-index: 6"
                :style="cleanTask"
              >
                <q-tooltip>
                  Clean Time {{ cleanTask.duration }} min
                </q-tooltip>
              </div>

              <div
                v-for="cleanTask in bookingWeek.cleanTasksScheduledSummary"
                :key="'cleanTasksScheduled' + cleanTask.top + ',' + cleanTask.left"
                :style="cleanTask"
                style="position: absolute;"
              />
            </template>
          </template>

          <div
            v-for="hourGridBox in hourGridBoxes"
            :key="'hour-grid-box-' + hourGridBox.left"
            :style="hourGridBox"
            class="hour-grid-box"
          />

          <div
            v-for="screenGridBox in screenGridBoxes"
            :key="'screen-grid-box-' + screenGridBox.top"
            :style="screenGridBox"
            class="screen-grid-box"
            @mouseover="mouseOverScreenId = screenGridBox.screenId; mouseOverDayNumber = screenGridBox.dayNumber; "
          />

          <div
            v-for="dayGridBox in dayGridBoxes"
            :key="'day-grid-box-' + dayGridBox.top"
            :style="dayGridBox"
            class="day-grid-box"
            @mouseover="mouseOverScreenId = dayGridBox.screenId; mouseOverDayNumber = dayGridBox.dayNumber; "
          />

          <showing-slot
            v-for="showingSlot in bookingWeek.displaySchedule"
            :key="showingSlot.id"
            :display-poster="!bookingWeek.loading"
            :showing-slot="showingSlot"
            :display-mode="displayMode"
            :show-times="showTimes"
            :show-whiskers="showWhiskers"
            :resource-planning="bookingWeek.showResources"
            :highlight-start="bookingWeek.auditView === 'start-times' && (showingSlot.bookingWeekMovie && showingSlot.bookingWeekMovie.id) === (bookingWeek.paintValue && bookingWeek.paintValue.id)"
            :dim="bookingWeek.paintValue && bookingWeek.auditView === 'start-times' && (showingSlot.bookingWeekMovie && showingSlot.bookingWeekMovie.id) !== (bookingWeek.paintValue && bookingWeek.paintValue.id)"
            :color-by="colorBy"
            @start-drag="handleShowingSlotDragStart"
            @end-drag="handleShowingSlotDragEnd"
            @double-click="handleDoubleClick"
          />
          <showing-slot
            v-if="bookingWeek.showingSlotGhost && !bookingWeek.auditView"
            :showing-slot="bookingWeek.showingSlotGhost"
            :display-mode="displayMode"
            :show-times="showTimes"
            :show-whiskers="showWhiskers"
            :color-by="colorBy"
          />
        </div>
      </div>
    </div>
    <showing-form ref="showingForm" />
    <showing-change-dialog
      ref="showingChangeDialog"
      @change="bookingWeek.fetch(true)"
    />
    <booking-week-copy-dialog
      ref="bookingWeekCopyDialog"
      :booking-week="bookingWeek"
    />
    <title-slot-dialog
      v-model="showTitleSlotDialog"
      :booking-week="bookingWeek"
      @select="bookingWeekMovie => bookingWeek.paintValue = bookingWeekMovie"
    />
    <q-dialog
      v-model="legend"
    >
      <q-card
        class="legend"
        style="width: 400px"
      >
        <q-card-section class="row items-center q-pb-none">
          <div class="text-h6">
            Legend
          </div>
          <q-space />
          <q-btn
            v-close-popup
            icon="close"
            flat
            round
            dense
          />
        </q-card-section>

        <q-card-section>
          <div class="column q-gutter-md">
            <div
              class="col showing-demo"
              style="border: dashed 2px; opacity: 0.5;"
            >
              Plan element, not yet a showing
              <q-icon
                name="crop_free"
                style="position: absolute; top: 3px; right: 3px;"
              />
            </div>
            <div
              class="col showing-demo"
              style="border-width: 2px;"
            >
              Showing, not published
            </div>
            <div
              class="col showing-demo"
              style="border-width: 4px;"
            >
              <q-icon
                name="lock"
                style="position: absolute; top: 3px; right: 3px; color: #222;"
              />
              Showing, published (locked, but no tickets sold)
            </div>
            <div
              class="col showing-demo"
              style="border-width: 4px;"
            >
              <q-icon
                name="lock"
                style="position: absolute; top: 3px; right: 3px; color: red;"
              />

              Showing, published (locked, tickets sold)
            </div>
            <div
              class="col showing-demo"
              style="border-width: 2px; opacity: 0.7"
            >
              <q-icon
                name="save"
                style="position: absolute; top: 3px; right: 3px;"
              />

              Showing, not saved
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
  <div
    v-else
    class="q-mt-md"
  >
    <div class="text-h5 row justify-center">
      No Default Site Schedule defined for the current period.
    </div>

    <div
      class="justify-center row q-mt-md"
    >
      <q-btn
        label="Go to Site Schedules"
        size="md"
        color="secondary"
        icon-right="open_in_new"
        @click="siteScheduleRedirect()"
      />
    </div>
  </div>
</template>

<script>

import { date } from 'quasar'
import { appEnv } from 'src/config'
import { headerHeight, minutesPerHour, totalItemHeight } from 'src/models/schedule/ShowingSlot'
import { START_HOUR, timeToMinutes } from 'src/models/schedule/TimeSlot'
import BookingWeekCopyDialog from 'src/modules/admin/booking-week/components/BookingWeekCopyDialog'
import LayoutSlot from 'src/modules/admin/booking-week/components/LayoutSlot'
import TitleSlotDialog from 'src/modules/admin/booking-week/components/TitleSlotDialog'
import ShowingForm from 'src/modules/admin/booking-week/ShowingForm'
import ShowingChangeDialog from 'src/modules/admin/components/ShowingChangeDialog'
import SubmenuOptions from 'src/modules/admin/components/SubmenuOptions'
import ShowingSlot from 'src/modules/admin/schedule/components/ShowingSlot'

const MINUS_KEY_CODE = 189
const PLUS_KEY_CODE = 187
const DELETE_KEY_CODE = 46
const BACKSPACE_KEY_CODE = 8

export default {
  name: 'Schedule',
  components: {
    ShowingSlot,
    LayoutSlot,
    ShowingForm,
    ShowingChangeDialog,
    SubmenuOptions,
    TitleSlotDialog,
    BookingWeekCopyDialog
  },
  props: {
    bookingWeek: {
      type: Object,
      required: true,
      default: null
    },
    previewLayout: {
      type: Object,
      required: false,
      default: null
    },
    previewingAction: {
      type: Boolean,
      required: false,
      default: false
    },
    showControls: {
      type: Boolean,
      required: false,
      default: true
    }
  },
  data () {
    return {
      legend: false,
      showTitleSlotDialog: false,
      displayMode: 'poster',
      colorBy: 'movie',

      dragging: false,
      draggingXStart: 0,
      draggingYStart: 0,
      draggingXEnd: 0,
      draggingYEnd: 0,
      mouseOverDayNumber: null,
      mouseOverScreenId: null,

      mouseOverMovieId: null,

      draggingShowingSlot: null,

      selectionAdding: false,

      totalItemHeight,

      contextMenuShown: false,

      clipboard: []
    }
  },
  configuration: ['currentCircuit', 'screens', 'timeSlots'],
  computed: {
    firstLayoutSlot () {
      const planElement = this.bookingWeek.planElements.data[0]
      const titleNumber = planElement?.numbers[0]
      const bookingWeekMovie = planElement.bookingWeekMovies.data[titleNumber - 1]

      return {
        planElement,
        titleNumber,
        bookingWeekMovie
      }
    },
    currentLayoutSlot () {
      if (!this.bookingWeek.paintValue) {
        return null
      }

      let layoutSlot = null
      _.some(this.bookingWeek.planElements.data, planElement => {
        return _.some(planElement.numbers, titleNumber => {
          if (this.bookingWeek.paintValue === planElement.bookingWeekMovies.data[titleNumber - 1]) {
            layoutSlot = {
              planElement,
              titleNumber,
              bookingWeekMovie: this.bookingWeek.paintValue
            }
            return true
          } else {
            return false
          }
        })
      })
      return layoutSlot
    },
    previousPaintValue () {
      if (!this.bookingWeek.paintValue) {
        return null
      }

      let paintValue = null
      _.some(this.bookingWeek.planElements.data, planElement => {
        return _.some(planElement.numbers, titleNumber => {
          if (this.bookingWeek.paintValue === planElement.bookingWeekMovies.data[titleNumber - 1]) {
            return true
          } else {
            paintValue = planElement.bookingWeekMovies.data[titleNumber - 1]
            return false
          }
        })
      })
      return paintValue
    },
    nextPaintValue () {
      if (!this.bookingWeek.paintValue) {
        return null
      }

      let paintValue = null
      let found = false
      _.some(this.bookingWeek.planElements.data, planElement => {
        return _.some(planElement.numbers, titleNumber => {
          if (found) {
            paintValue = planElement.bookingWeekMovies.data[titleNumber - 1]
            return true
          } else if (this.bookingWeek.paintValue === planElement.bookingWeekMovies.data[titleNumber - 1]) {
            found = true

            return false
          } else {
            return false
          }
        })
      })
      return paintValue
    },
    displayShowingChangeOption () {
      return this.bookingWeek.selected.length === 1 && this.bookingWeek.selected[0].showing
    },
    showingChangeDisabled () {
      return this.bookingWeek.selected[0].showing.published || this.isSelectedMarathon || this.hasUnreservedSeatingChart
    },
    isSelectedMarathon () {
      return this.bookingWeek.selected[0].showing.isMarathon
    },
    hasUnreservedSeatingChart () {
      const showing = this.bookingWeek.selected[0].showing
      return !showing.reservedSeating && !!showing.screen?.seatChart
    },
    needsShowingChangeTemplateId () {
      return appEnv === 'development' ? false : this.currentCircuit.customerioWorkspaceIsConfigured && !this.currentSite.customerioShowingChangeTemplateId
    },
    showingChangeDisabledTooltip () {
      if (this.hasUnreservedSeatingChart) return 'Modify Showing Tool is not yet available for showing with seating charts and reserved seating disabled.'
      if (this.isSelectedMarathon) return 'Modify Showing Tool is not yet available for marathon showings.'
      if (this.showingChangeDisabled) return 'This showing must be unpublished before changes can be made.'
      if (this.bookingWeek.isDirty) return 'Please save your changes before continuing.'
      if (this.needsShowingChangeTemplateId) return 'Missing Customer.io template ID for showing change email. Contact support for assistance.'

      return ''
    },
    scheduleWidth () {
      return 24 * minutesPerHour * this.bookingWeek.pixelsPerMinute
    },
    computedTimeSlots () {
      const timeSlots = []
      const top = 0
      _.each(this.timeSlots.data, (timeSlot) => {
        const startTimeMinutesOffset = this.timeInPixels(timeSlot.startTime)
        const endTimeMinutesOffset = this.timeInPixels(timeSlot.endTime)
        const targetTimeMinutesOffset = this.timeInPixels(timeSlot.targetTime)

        timeSlots.push({
          id: timeSlot.id,
          name: timeSlot.name,
          top,
          left: startTimeMinutesOffset - 1,
          width: endTimeMinutesOffset - startTimeMinutesOffset + this.bookingWeek.pixelsPerMinute - 1,
          targetTimeOffset: targetTimeMinutesOffset - startTimeMinutesOffset
        })
      })

      return timeSlots
    },
    hours () {
      const hours = []
      for (let hourCount = 0; hourCount < 24; hourCount += 1) {
        const time = date.buildDate({ hours: START_HOUR + hourCount, minutes: 0 })
        hours.push({
          top: 30 + 'px',
          left: (hourCount * minutesPerHour * this.bookingWeek.pixelsPerMinute) + 'px',
          width: (minutesPerHour * this.bookingWeek.pixelsPerMinute) + 'px',
          label: date.formatDate(time, 'h:mm a')
        })
      }

      return hours
    },
    hourGridBoxes () {
      const gridBoxes = []
      for (let hourCount = 0; hourCount < 24; hourCount += 1) {
        gridBoxes.push({
          top: headerHeight + 'px',
          left: (hourCount * minutesPerHour * this.bookingWeek.pixelsPerMinute) + 'px',
          width: (minutesPerHour * this.bookingWeek.pixelsPerMinute) + 'px',
          'z-index': 1
        })
      }

      return gridBoxes
    },
    dayGridBoxes () {
      if (this.bookingWeek.filteredScreens.length === 0) {
        return
      }

      const gridBoxes = []

      if (this.bookingWeek.by === 'screen') {
        const daysInWeek = 7
        const screenHeight = daysInWeek * totalItemHeight
        const dayHeight = totalItemHeight
        _.each(this.bookingWeek.filteredScreens, (screen, screenIndex) => {
          _.each(this.bookingWeek.daysOfWeekNumbers, (dayNumber, dayIndex) => {
            gridBoxes.push({
              dayNumber,
              screenId: screen.id,
              top: headerHeight + screenHeight * screenIndex + dayIndex * dayHeight + 'px',
              height: totalItemHeight + 'px',
              'background-color': dayIndex % 2 ? '#99999955' : '#99999933',
              border: 'solid 1px #CCC',
              'z-index': 2
            })
          })
        })
      } else {
        const dayHeight = this.bookingWeek.filteredScreens.length * totalItemHeight + this.bookingWeek.resourceGraphHeight
        _.each(this.bookingWeek.daysOfWeekNumbers, (dayNumber, dayIndex) => {
          gridBoxes.push({
            dayNumber,
            top: headerHeight + dayIndex * dayHeight + 'px',
            height: dayHeight + 'px',
            border: 'solid 1px black',
            'border-top': 'none',
            'z-index': 0
          })
        })
      }

      return gridBoxes
    },
    screenGridBoxes () {
      const gridBoxes = []
      if (this.bookingWeek.by === 'day') {
        const resourceGraphHeight = this.bookingWeek.resourceGraphHeight
        const dayHeight = this.bookingWeek.filteredScreens.length * totalItemHeight + resourceGraphHeight
        const screenHeight = totalItemHeight
        _.each(this.bookingWeek.filteredScreens, (screen, screenIndex) => {
          _.each(this.bookingWeek.daysOfWeekNumbers, (dayNumber, dayIndex) => {
            gridBoxes.push({
              dayNumber,
              screenId: screen.id,
              top: headerHeight + (screenHeight * screenIndex) + (dayIndex * dayHeight) + resourceGraphHeight + 'px',
              height: totalItemHeight + 'px',
              'background-color': dayIndex % 2 ? '#99999955' : '#99999933',
              border: 'solid 1px #CCC',
              'z-index': 2
            })
          })
        })
      } else {
        const daysInWeek = 7
        const screenHeight = daysInWeek * totalItemHeight
        _.each(this.bookingWeek.filteredScreens, (screen, screenIndex) => {
          gridBoxes.push({
            screenId: screen.id,
            top: headerHeight + screenIndex * screenHeight + 'px',
            height: screenHeight + 'px',
            border: 'solid 1px black',
            'border-top': 'none',
            'z-index': 0
          })
        })
      }

      return gridBoxes
    },
    layout () {
      if (this.previewingAction) {
        return this.previewLayout
      } else {
        return this.bookingWeek.displayLayout
      }
    },
    draggingTop () {
      return Math.min(this.draggingYStart, this.draggingYEnd)
    },
    draggingLeft () {
      return Math.min(this.draggingXStart, this.draggingXEnd)
    },
    draggingHeight () {
      return Math.abs(this.draggingYStart - this.draggingYEnd)
    },
    draggingWidth () {
      return Math.abs(this.draggingXStart - this.draggingXEnd)
    },
    selectionList () {
      return _.union(this.totalSelectionList, this.currentSelectionList)
    },
    screenFilterOptions () {
      const options = [{ label: 'All', value: null }]
      _.each(this.screens.data, screen => {
        options.push({
          label: screen.displayOrder,
          value: screen.id
        })
      })
      return options
    },
    dayFilterOptions () {
      const options = this.bookingWeek.auditView ? [] : [{ label: 'All', value: null }]
      _.each(this.currentCircuit.daysOfWeek, (day, index) => {
        options.push({
          label: date.formatDate(date.addToDate(this.bookingWeek.date, { date: index }), 'ddd Do'),
          value: this.currentCircuit.daysOfWeekNumbers[index]
        })
      })
      return options
    },
    daysOfWeekNumbers () {
      if (this.bookingWeek.by !== 'day' || this.bookingWeek.dayFilter === null) {
        return this.currentCircuit.daysOfWeekNumbers
      }
      return _.filter(this.currentCircuit.daysOfWeekNumbers, dayNumber => dayNumber === this.bookingWeek.dayFilter)
    },
    filteredScreens () {
      if (this.bookingWeek.by !== 'screen' || this.bookingWeek.screenFilter === null) {
        return this.screens.data
      }

      return _.filter(this.screens.data, screen => screen.id === this.bookingWeek.screenFilter)
    },
    canMoveDay () {
      const allSameDay = _(this.bookingWeek.selectedWithEditableTime).map('dayNumber').uniq().value().length === 1
      return allSameDay
    },
    canMoveScreen () {
      const allSameScreen = _(this.bookingWeek.selectedWithEditableTime).map('screen.id').uniq().value().length === 1
      return allSameScreen
    },
    showTimes () {
      return this.bookingWeek.pixelsPerMinute >= 0.5
    },
    showWhiskers () {
      return this.bookingWeek.pixelsPerMinute >= 0.5 && !this.bookingWeek.showResources
    },
    isEditingShowing () {
      return this.$refs.showingForm?.$refs?.modelForm?.showDialog
    }
  },
  watch: {
    mouseOverDayNumber () {
      if (this.mouseDown && this.draggingShowingSlot) {
        this.handleDragOverDay()
      }
    },
    mouseOverScreenId () {
      if (this.mouseDown && this.draggingShowingSlot) {
        this.handleDragOverScreen()
      }
    },
    contextMenuShown () {
      this.handleDragEnd()
    },
    'bookingWeek.pixelsPerMinute' () {
      this.scrollScheduleToCenter()
    },
    'taskPlanningForDate.loading' () {
      if (this.taskPlanningForDate.loading) return

      this.bookingWeek.taskPlanningForWeek.data = [this.taskPlanningForDate]
      this.bookingWeek.taskPlanningForWeek.setAndTriggerCollectionChange(this.bookingWeek.taskPlanningForWeek.data)
    },
    'bookingWeek.loading' () {
      !this.bookingWeek.loading && this.reloadResources()
    },
    'bookingWeek.auditView' () {
      this.bookingWeek.showResources = false

      if (this.bookingWeek.auditView === 'resources') {
        this.bookingWeek.paintValue = null
        this.bookingWeek.scheduleMode = 'actual'
        this.bookingWeek.showResources = true
      } else if (this.bookingWeek.auditView === 'start-times') {
        this.bookingWeek.scheduleMode = 'actual'
        if (!this.bookingWeek.paintValue) {
          // Select first movie
          this.bookingWeek.paintValue = this.bookingWeek.planElements.data[0].bookingWeekMovies.data[0]
        }
      } else {
        this.bookingWeek.paintValue = null
        this.bookingWeek.scheduleMode = 'plan-and-actual'
      }
    },
    'bookingWeek.paintValue' () {
      // If auditing and switching to paint nothing, assume they are going back to building the schedule
      if (!this.bookingWeek.paintValue && this.bookingWeek.auditView) {
        this.bookingWeek.auditView = null
      }
    }
  },
  mounted () {
    this.isMounted = true
    this.scrollScheduleToCenter()

    this.bookingWeek.dayFilter = this.currentCircuit.dayOfWeekStart
  },
  async created () {
    _.extend(this, { date })
    window.addEventListener('resize', this.scrollScheduleToCenter)
    document.addEventListener('keydown', this.handleKeyPress)
    await this.$getHighchartsAsync()
  },
  unmounted () {
    window.removeEventListener('resize', this.scrollScheduleToCenter)
    document.removeEventListener('keydown', this.handleKeyPress)
  },
  methods: {
    buildDay () {
      if (!this.draggingShowingSlot) { this.bookingWeek.selectBy({ dayNumber: this.mouseOverDayNumber }) }
      this.bookingWeek.convertSelectedToShowings()
    },
    scrollScheduleToCenter () {
      if (this.$refs.wrapper.clientWidth < this.scheduleWidth) {
        this.$refs.wrapper.scrollLeft = this.scheduleWidth / 2 - this.$refs.wrapper.clientWidth / 2
      }
    },
    handleKeyPress (event) {
      if (event.shiftKey && event.keyCode === MINUS_KEY_CODE) {
        this.zoom(-1)
        return false
      }
      if (event.shiftKey && event.keyCode === PLUS_KEY_CODE) {
        this.zoom(1)
        return false
      }
      if (event.keyCode === DELETE_KEY_CODE || event.keyCode === BACKSPACE_KEY_CODE) {
        // If showing form is open we don't want backspace/delete to delete showing
        if (!this.isEditingShowing) {
          this.bookingWeek.removeSelectedShowings()
        }
        return false
      }

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        const moved = this.moveScreens(event.key === 'ArrowUp' ? -1 : 1)
        if (moved) {
          event.preventDefault()
          return false
        }
      }

      const distance = event.shiftKey ? 30 : (this.bookingWeek.snapTo || 1)
      if (event.key === 'ArrowLeft') return this.moveSelection(distance, -1)
      if (event.key === 'ArrowRight') return this.moveSelection(distance, 1)
    },
    moveSelection (distance = 5, direction = 1) {
      if (this.bookingWeek.selected.length === 0) {
        this.$refs.wrapper.scrollLeft += distance * direction
      } else {
        _.each(this.bookingWeek.selectedWithEditableTime, showingSlot => {
          showingSlot.showing.addMinutes(distance * direction)
        })
      }
    },
    siteScheduleRedirect () {
      window.location.hash = '#/settings/schedule-setup/site-schedules'
    },
    moveScreens (direction = 1) {
      if (this.bookingWeek.by === 'day') {
        const allMovable = _.every(this.bookingWeek.selectedWithEditableTime, showingSlot => {
          const index = _.findIndex(this.screens.data, showingSlot.showing.screen)
          if (direction === -1 && index === 0) return false
          if (direction === 1 && index >= this.screens.data.length - 1) return false
          return true
        })
        if (!allMovable) return

        _.each(this.bookingWeek.selectedWithEditableTime, showingSlot => {
          const index = _.findIndex(this.screens.data, showingSlot.showing.screen)
          showingSlot.showing.screen = this.screens.data[index + 1 * direction]
        })

        return true
      }
    },
    zoom (direction) {
      if (direction === -1 && this.bookingWeek.pixelsPerMinute <= 1) return
      if (direction === 1 && this.bookingWeek.pixelsPerMinute >= 5) return

      this.bookingWeek.pixelsPerMinute += 1 * direction * 0.1
    },
    isPreview (screenId, dayNumber, timeSlotId) {
      if (!this.previewingAction) {
        return false
      }

      const currentValue = this.bookingWeek.displayLayout[screenId] && this.bookingWeek.displayLayout[screenId][timeSlotId] && this.bookingWeek.displayLayout[screenId][timeSlotId][dayNumber]
      const previewValue = this.previewLayout[screenId] && this.previewLayout[screenId][timeSlotId] && this.previewLayout[screenId][timeSlotId][dayNumber]

      // Handle different types of missing values, undefined, false and null
      if ((!currentValue && !previewValue) || (!currentValue?.planElement && !previewValue?.planElement) || (!currentValue?.bookingWeekMovie && !previewValue?.bookingWeekMovie)) {
        return false
      }

      if (!currentValue || !previewValue || !currentValue.planElement || !previewValue.planElement || !currentValue.bookingWeekMovie || !previewValue.bookingWeekMovie) {
        return true
      }

      return !(currentValue.planElement.id === previewValue.planElement.id &&
        currentValue.titleNumber === previewValue.titleNumber &&
        currentValue.bookingWeekMovie.id === previewValue.bookingWeekMovie.id)
    },
    timeInPixels (time) {
      return timeToMinutes(time) * this.bookingWeek.pixelsPerMinute
    },
    handleShowingSlotDragStart (showingSlot) {
      this.draggingShowingSlot = showingSlot
      this.mouseOverMovieId = showingSlot?.movie?.id || null
    },
    handleShowingSlotDragEnd (showingSlot) {
      this.draggingShowingSlot = null
    },
    async handleDragStart (event) {
      const rect = event.currentTarget.getBoundingClientRect()
      this.mouseDown = true

      if (this.draggingShowingSlot) {
        _.each(this.bookingWeek.selectedWithShowings, showingSlot => {
          if (!showingSlot.showing) return

          showingSlot.showing.editing = true
        })
        await this.$nextTick()
        // If this is already selected, may be a part of a larger selection, if not, select it
        if (!this.draggingShowingSlot.selected) {
          if (!event.ctrlKey && !event.metaKey) {
            this.bookingWeek.clearCurrentSelection()
            this.bookingWeek.clearFinalSelection()
          }
          this.draggingShowingSlot.finalSelection = true
        }
        this.selectedShowingClones = _.map(this.bookingWeek.selectedWithShowings, showingSlot => {
          const clone = showingSlot.showing.copy()
          clone.published = false
          clone.ticketsSold = 0
          return clone
        })
        this.draggingXStart = event.clientX - rect.left
      } else {
        // Drag selection
        if (event.ctrlKey || event.metaKey) {
          this.selectionAdding = true
        } else {
          this.selectionAdding = false
          this.bookingWeek.clearCurrentSelection()
          this.bookingWeek.clearFinalSelection()
        }

        this.draggingXStart = event.clientX - rect.left
        this.draggingYStart = event.clientY - rect.top
      }
    },
    handleDragEnd (event) {
      this.mouseDown = false
      if (this.draggingShowingSlot) {
        _.each(this.bookingWeek.selectedWithShowings, showingSlot => {
          if (!showingSlot.showing) return

          showingSlot.showing.editing = false
        })

        this.draggingShowingSlot = null
        this.selectedShowingClones = []
        this.clonesMerged = false
      } else {
        this.dragging = false
        this.bookingWeek.finalizeCurrentSelection()
      }
    },
    handleMouseMove (event) {
      if (this.mouseDown && event.buttons === 0) {
        this.handleDragEnd()
        return
      }

      const rect = event.currentTarget.getBoundingClientRect()

      if (this.mouseDown && this.draggingShowingSlot) {
        const draggingX = event.clientX - rect.left

        if (this.bookingWeek.snapTo && Math.abs(draggingX - this.draggingXStart) < this.bookingWeek.snapTo * this.bookingWeek.pixelsPerMinute) {
          return
        }

        _.each(this.bookingWeek.selectedWithEditableTime, showingSlot => {
          showingSlot.showing.addMinutes((draggingX - this.draggingXStart) / this.bookingWeek.pixelsPerMinute)
        })

        if (this.bookingWeek.snapTo) {
          this.bookingWeek.snapToMinutes()
        }

        const copying = event.ctrlKey || event.metaKey
        this.handleCopyWhileDragging(copying)
        this.draggingXStart = draggingX
      } else if (this.mouseDown) {
        if (event.buttons === 0) {
          this.handleDragEnd()
          return
        }

        this.dragging = true

        this.draggingXEnd = event.clientX - rect.left
        this.draggingYEnd = event.clientY - rect.top

        this.handleSelection()
      } else if (this.bookingWeek.paintValue) {
        this.bookingWeek.validateShowingSlotGhost(this.mouseOverDayNumber, this.mouseOverScreenId, event.clientX - rect.left)
      }
    },
    handleMouseLeave () {
      if (this.bookingWeek.showingSlotGhost) {
        this.bookingWeek.destroyShowingSlotGhost()
      }
    },
    handleClick () {
      if (this.bookingWeek.paintValue && this.bookingWeek.paintValue.movie && this.bookingWeek.showingSlotGhost && !this.bookingWeek.auditView) {
        this.bookingWeek.convertToShowings([this.bookingWeek.showingSlotGhost])
      }
    },
    handleCopyWhileDragging (copying) {
      if (copying && !this.clonesMerged) {
        // Make sure clone is added because we are copying
        _.each(this.selectedShowingClones, showing => {
          this.bookingWeek.showings.add(showing)
        })
        this.clonesMerged = true
      } else if (!copying && this.clonesMerged) {
        // Remove clones
        _.each(this.selectedShowingClones, showing => {
          this.bookingWeek.showings.remove(showing)
        })
        this.clonesMerged = false
      }
    },
    handleSelection () {
      const minX = this.draggingLeft
      const maxX = this.draggingLeft + this.draggingWidth
      const minY = this.draggingTop
      const maxY = this.draggingTop + this.draggingHeight

      this.bookingWeek.clearCurrentSelection()

      _.each(this.bookingWeek.displaySchedule, showingSlot => {
        if ((showingSlot.top + showingSlot.height) > minY && showingSlot.top < maxY &&
            (showingSlot.left + showingSlot.width) > minX && showingSlot.left < maxX) {
          showingSlot.currentSelection = true
        }
      })
    },
    handleDragOverDay (event) {
      if (this.canMoveDay) {
        _.each(this.bookingWeek.selectedWithEditableTime, showingSlot => {
          showingSlot.showing.setDayNumber(this.mouseOverDayNumber, this.bookingWeek.startDayNumber)
        })
      }
    },
    handleDragOverScreen (event) {
      if (this.canMoveScreen) {
        const screen = _.find(this.screens.data, screen => screen.id === this.mouseOverScreenId)
        _.each(this.bookingWeek.selectedWithEditableTime, showingSlot => {
          showingSlot.showing.screen = screen
        })
      }
    },
    handleDoubleClick (showing) {
      if (!showing) {
        this.bookingWeek.convertSelectedToShowings()
      } else {
        this.edit(showing)
      }
    },
    copy () {
      this.clipboard = _.clone(this.bookingWeek.selectedWithShowings)
    },
    paste () {
      _.each(this.clipboard, showingSlot => {
        const newShowing = showingSlot.showing.copy()
        if (newShowing.isMarathon) {
          newShowing.showings = []
          _.each(showingSlot.showing.showings.data, marathonShowing => {
            newShowing.showings.push(marathonShowing.copy())
          })
        }
        newShowing.published = false
        newShowing.ticketsSold = 0
        newShowing.setBookingWeekDate(this.bookingWeek.date)
        newShowing.setDayNumber(this.mouseOverDayNumber, this.bookingWeek.startDayNumber)
        this.bookingWeek.showings.add(newShowing)
        this.bookingWeek.handleNewShowing(newShowing)
      })
    },
    edit (showing) {
      if (!this.bookingWeek.selectedWithShowings.length) {
        return
      }
      if (!showing) {
        showing = this.bookingWeek.selectedWithShowings[0].showing
      }
      if (this.bookingWeek.selectedWithShowings.length > 1) {
        this.$refs.showingForm.bulkEditing = true
        this.$refs.showingForm.showingSlots = this.bookingWeek.selectedWithShowings
      } else {
        this.$refs.showingForm.bulkEditing = false
        this.$refs.showingForm.showingSlots = []
      }
      this.$refs.showingForm.setCollection(this.bookingWeek.showings)
      this.$refs.showingForm.show(showing)
    },
    reloadResources () {
      this.bookingWeek.allowShowResources && this.bookingWeek.showResources && this.taskPlanningForDate.fetch(true)
    }
  },
  models: {
    taskPlanningForDate: {
      arriveSeries: true,
      employeeDemandSeries: true,
      cleaningEmployeesSeries: true,
      arriveSeriesByShowingId: true,
      arriveSeriesByShowingStartMinute: true,
      registerEmployeeDemandSeries: true,
      cleanTasks: {
        showing: {
          screen: 'default'
        }
      },
      variables () {
        return {
          date: date.formatDate(this.bookingWeek.dateOfDayFilter, 'YYYY-MM-DD'),
          options: {
          },
          skip: !this.bookingWeek.allowShowResources || !this.bookingWeek.showResources
        }
      }
    },
    currentSite: {
      customerioShowingChangeTemplateId: true,
      immediate: true
    }
  }
}

</script>

<style lang="scss" scoped>
  .button-title {
    font-size: 11px;
    font-weight: bold;
  }

  .wrapper {
    overflow-x: auto;
    overflow-y: hidden;
    border: solid 1px #AAA;
    border-radius: 5px;
    min-height: 300px;
    background-color: #EEE;
  }

  .schedule {
    height: 100%;
    margin-left: auto;
    margin-right: auto;
    position: relative;
    background-color: #FFF;
  }

  .label {
    height: 30px;
    position: absolute;
    border: solid 1px #AAA;
  }

  .time-slot {
    border: solid 1px #AAA;
    position: absolute;
    height: 30px;
    overflow: hidden;
  }

  .target-time {
    position: absolute;
    top: 10px;
  }

  .hour-grid-box {
    border: dashed 1px #CCC;
    position: absolute;
    height: 100%;
  }

  .day-grid-box {
    position: absolute;
    width: 100%;
  }

  .screen-grid-box {
    position: absolute;
    width: 100%;
  }

  .showing-demo {
    position: relative;
    padding: 10px;
    border: solid 2px;
  }

  .selected-button {
    background-color: #BBB !important;
  }
</style>
